import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft,
  Download,
  FileText,
  DollarSign,
  Calendar,
  Tag,
  AlertCircle,
  Loader2,
  Users,
  CreditCard,
  User,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle,
  XCircle,
  Trash2,
  Clock,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePagination } from "@/hooks/usePagination";
import { createPayoutRequest, updatePayoutStatus } from "@/api/collaborator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PayoutActionDialog, {
  ReceiptPreviewDialog,
} from "@/components/PayoutActionDialog";
import PayoutSelectionDialog from "@/components/PayoutSelectionDialog";

// Type definitions
interface UserProfile {
  id: number;
  uid: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  age_group: string;
  is_active: boolean;
  registered_at: string;
}

interface Promotion {
  id: number;
  name: string;
  description: string | null;
  code: string | null;
  discount_value: number;
  discount_type: "percentage" | "fixed";
  start_date: string;
  end_date: string | null;
  max_uses: number | null;
  current_uses: number;
  min_purchase_amount: number | null;
  applicable_plans: number[];
  is_active: boolean;
  collaborator_id: number | null;
  collaborator_commission_value: number | null;
  collaborator_commission_type: "percentage" | "fixed" | null;
  created_at: string;
  updated_at: string;
}

interface CollaboratorEarning {
  id: number;
  collaborator_id: number;
  promotion_id: number;
  user_subscription_id: number;
  amount_earned: number;
  commission_type_at_time_of_transaction: "percentage" | "fixed";
  commission_at_time_of_transaction: number;
  payout_id: number | null;
  created_at: string;
  promotions?: {
    id: number;
    name: string;
    code: string;
  };
  user_subscriptions?: {
    id: number;
    price: number;
    currency_code: string;
    user_id: number;
    subscription_plan_id: number;
    status: string;
    subscription_start: string;
  };
}

interface CollaboratorPayout {
  id: number;
  collaborator_id: number;
  amount: number;
  status: "pending" | "completed" | "failed";
  payout_date: string | null;
  receipt_url: string | null;
  notes: string | null;
  created_at: string;
}

interface CustomerProfile {
  id: number;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  duration: number;
  duration_type: string;
}

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
};

const CollaboratorDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("earnings");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingPayout, setIsCreatingPayout] = useState(false);
  const [isUpdatingPayout, setIsUpdatingPayout] = useState<number | null>(null);
  const [isDeletingPayout, setIsDeletingPayout] = useState<number | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [earnings, setEarnings] = useState<CollaboratorEarning[]>([]);
  const [payouts, setPayouts] = useState<CollaboratorPayout[]>([]);
  const [customerProfiles, setCustomerProfiles] = useState<
    Record<number, CustomerProfile>
  >({});
  const [payoutLookup, setPayoutLookup] = useState<
    Record<number, CollaboratorPayout>
  >({});
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingEarnings: 0,
    processingEarnings: 0,
    totalPaid: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    activePromotions: 0,
    totalPromotions: 0,
    totalSubscriptions: 0,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [subscriptionPlanInfo, setSubscriptionPlanInfo] = useState<
    Record<number, SubscriptionPlan>
  >({});

  const fetchStatsData = useCallback(async () => {
    if (!id) return null;

    try {
      // Fetch earnings data for stats calculation only (minimal fields)
      const { data: earningsStatsData, error: earningsStatsError } =
        await supabase
          .from("collaborator_earnings")
          .select("id, amount_earned, payout_id")
          .eq("collaborator_id", parseInt(id));

      if (earningsStatsError) throw earningsStatsError;

      // Fetch payouts for stats calculation
      const { data: payoutsStatsData, error: payoutsStatsError } =
        await supabase
          .from("collaborator_payouts")
          .select("id, amount, status")
          .eq("collaborator_id", parseInt(id));

      if (payoutsStatsError) throw payoutsStatsError;

      // Create payout lookup for stats calculation
      const payoutMap: Record<
        number,
        {
          id: number;
          amount: number;
          status: "pending" | "completed" | "failed";
        }
      > = {};
      payoutsStatsData?.forEach((payout) => {
        payoutMap[payout.id] = payout;
      });

      // Calculate enhanced stats
      const totalEarnings = earningsStatsData
        ? earningsStatsData.reduce(
            (sum, item) => sum + (item.amount_earned || 0),
            0
          )
        : 0;

      const pendingEarnings = earningsStatsData
        ? earningsStatsData
            .filter((item) => !item.payout_id)
            .reduce((sum, item) => sum + (item.amount_earned || 0), 0)
        : 0;

      // Calculate processing earnings (has payout_id and payout status is pending)
      const processingEarnings = earningsStatsData
        ? earningsStatsData
            .filter((item) => {
              if (!item.payout_id) return false;
              const payout = payoutMap[item.payout_id];
              return payout && payout.status === "pending";
            })
            .reduce((sum, item) => sum + (item.amount_earned || 0), 0)
        : 0;

      // Calculate total paid (has payout_id and payout status is completed)
      const totalPaid = earningsStatsData
        ? earningsStatsData
            .filter((item) => {
              if (!item.payout_id) return false;
              const payout = payoutMap[item.payout_id];
              return payout && payout.status === "completed";
            })
            .reduce((sum, item) => sum + (item.amount_earned || 0), 0)
        : 0;

      const totalPayouts = payoutsStatsData
        ? payoutsStatsData.reduce((sum, item) => sum + (item.amount || 0), 0)
        : 0;

      const pendingPayouts = payoutsStatsData
        ? payoutsStatsData
            .filter((item) => item.status === "pending")
            .reduce((sum, item) => sum + (item.amount || 0), 0)
        : 0;

      return {
        totalEarnings,
        pendingEarnings,
        processingEarnings,
        totalPaid,
        totalPayouts,
        pendingPayouts,
        totalSubscriptions: earningsStatsData ? earningsStatsData.length : 0,
      };
    } catch (error) {
      console.error("Error fetching stats data:", error);
      throw error;
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      // Fetch user details, promotions, and stats in parallel
      const [
        { data: userData, error: userError },
        { data: promotionsData, error: promotionsError },
        statsData,
      ] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", parseInt(id)).single(),
        supabase
          .from("promotions")
          .select("*")
          .eq("collaborator_id", parseInt(id))
          .order("created_at", { ascending: false }),
        fetchStatsData(),
      ]);

      if (userError) throw userError;
      if (promotionsError) throw promotionsError;

      setUser(userData);
      setPromotions(promotionsData || []);

      // Fetch earnings with user subscription information for table display
      const { data: earningsData, error: earningsError } = await supabase
        .from("collaborator_earnings")
        .select(
          `
          *,
          promotions (
            id,
            name,
            code
          ),
          user_subscriptions (
            id,
            price,
            currency_code,
            user_id,
            subscription_plan_id,
            status,
            subscription_start
          )
        `
        )
        .eq("collaborator_id", parseInt(id))
        .order("created_at", { ascending: false });

      if (earningsError) throw earningsError;
      setEarnings(earningsData || []);

      // If we have earnings data with subscription plan IDs, fetch plan information separately
      if (earningsData && earningsData.length > 0) {
        // Extract subscription plan IDs
        const planIds = Array.from(
          new Set(
            earningsData
              .filter((item) => item.user_subscriptions?.subscription_plan_id)
              .map((item) => item.user_subscriptions?.subscription_plan_id)
          )
        ).filter(Boolean);

        if (planIds.length > 0) {
          // Fetch subscription plans
          const { data: plansData, error: plansError } = await supabase
            .from("subscription_plans")
            .select("id, name, duration, duration_type")
            .in("id", planIds);

          if (plansError) throw plansError;

          // Create lookup object for plans
          const plansMap = {};
          plansData?.forEach((plan) => {
            plansMap[plan.id] = plan;
          });

          setSubscriptionPlanInfo(plansMap);
        }
      }

      // If we have earnings data, fetch the customer profiles separately
      if (earningsData && earningsData.length > 0) {
        // Extract unique user IDs from subscriptions
        const customerIds = Array.from(
          new Set(
            earningsData
              .filter(
                (item) =>
                  item.user_subscriptions && item.user_subscriptions.user_id
              )
              .map((item) => item.user_subscriptions.user_id)
          )
        );

        if (customerIds.length > 0) {
          // Fetch all customer profiles at once
          const { data: customersData, error: customersError } = await supabase
            .from("profiles")
            .select("id, name, email, avatar_url")
            .in("id", customerIds);

          if (customersError) throw customersError;

          // Create a lookup object for quick access by ID
          const customersMap: Record<number, CustomerProfile> = {};
          customersData?.forEach((customer) => {
            customersMap[customer.id] = customer;
          });

          setCustomerProfiles(customersMap);
        }
      }

      // Fetch payouts for table display
      const { data: payoutsData, error: payoutsError } = await supabase
        .from("collaborator_payouts")
        .select("*")
        .eq("collaborator_id", parseInt(id))
        .order("created_at", { ascending: false });

      if (payoutsError) throw payoutsError;
      setPayouts(payoutsData || []);

      // Create payout lookup for earnings status determination
      const payoutMap: Record<number, CollaboratorPayout> = {};
      payoutsData?.forEach((payout) => {
        payoutMap[payout.id] = payout;
      });
      setPayoutLookup(payoutMap);

      // Set stats from the separate stats calculation
      if (statsData) {
        setStats({
          ...statsData,
          activePromotions: promotionsData
            ? promotionsData.filter((item) => item.is_active).length
            : 0,
          totalPromotions: promotionsData ? promotionsData.length : 0,
        });
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching collaborator data:", error);
      toast({
        title: "Error",
        description: "Failed to load collaborator data. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  }, [id, toast, fetchStatsData]);

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh only stats data (more efficient for payout operations)
  const refreshStats = async () => {
    if (!id) return;

    try {
      const statsData = await fetchStatsData();
      if (statsData) {
        setStats((prevStats) => ({
          ...statsData,
          activePromotions: prevStats.activePromotions,
          totalPromotions: prevStats.totalPromotions,
        }));
      }
    } catch (error) {
      console.error("Error refreshing stats:", error);
      // Fallback to full refresh if stats refresh fails
      await fetchData();
    }
  };

  // Refresh payouts and stats data (efficient for payout operations)
  const refreshPayoutsAndStats = async () => {
    if (!id) return;

    try {
      // Fetch payouts and stats in parallel
      const [{ data: payoutsData, error: payoutsError }, statsData] =
        await Promise.all([
          supabase
            .from("collaborator_payouts")
            .select("*")
            .eq("collaborator_id", parseInt(id))
            .order("created_at", { ascending: false }),
          fetchStatsData(),
        ]);

      if (payoutsError) throw payoutsError;

      setPayouts(payoutsData || []);

      // Create payout lookup for earnings status determination
      const payoutMap: Record<number, CollaboratorPayout> = {};
      payoutsData?.forEach((payout) => {
        payoutMap[payout.id] = payout;
      });
      setPayoutLookup(payoutMap);

      // Set stats from the separate stats calculation
      if (statsData) {
        setStats((prevStats) => ({
          ...statsData,
          activePromotions: prevStats.activePromotions,
          totalPromotions: prevStats.totalPromotions,
        }));
      }
    } catch (error) {
      console.error("Error refreshing payouts and stats:", error);
      // Fallback to full refresh if partial refresh fails
      await fetchData();
    }
  };

  // Refresh earnings, payouts and stats data (for payout creation operations)
  const refreshEarningsPayoutsAndStats = async () => {
    if (!id) return;

    try {
      // Fetch earnings, payouts and stats in parallel
      const [
        { data: earningsData, error: earningsError },
        { data: payoutsData, error: payoutsError },
        statsData,
      ] = await Promise.all([
        supabase
          .from("collaborator_earnings")
          .select(
            `
          *,
          promotions (
            id,
            name,
            code
          ),
          user_subscriptions (
            id,
            price,
            currency_code,
            user_id,
            subscription_plan_id,
            status,
            subscription_start
          )
        `
          )
          .eq("collaborator_id", parseInt(id))
          .order("created_at", { ascending: false }),
        supabase
          .from("collaborator_payouts")
          .select("*")
          .eq("collaborator_id", parseInt(id))
          .order("created_at", { ascending: false }),
        fetchStatsData(),
      ]);

      if (earningsError) throw earningsError;
      if (payoutsError) throw payoutsError;

      setEarnings(earningsData || []);
      setPayouts(payoutsData || []);

      // Create payout lookup for earnings status determination
      const payoutMap: Record<number, CollaboratorPayout> = {};
      payoutsData?.forEach((payout) => {
        payoutMap[payout.id] = payout;
      });
      setPayoutLookup(payoutMap);

      // Set stats from the separate stats calculation
      if (statsData) {
        setStats((prevStats) => ({
          ...statsData,
          activePromotions: prevStats.activePromotions,
          totalPromotions: prevStats.totalPromotions,
        }));
      }
    } catch (error) {
      console.error("Error refreshing earnings, payouts and stats:", error);
      // Fallback to full refresh if partial refresh fails
      await fetchData();
    }
  };

  // Get earning status based on payout_id and payout status
  const getEarningStatus = (earning: CollaboratorEarning) => {
    if (!earning.payout_id) {
      return {
        status: "pending",
        className: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      };
    }

    const payout = payoutLookup[earning.payout_id];
    if (!payout) {
      return {
        status: "unknown",
        className: "bg-gray-500/10 text-gray-500 border-gray-500/20",
      };
    }

    switch (payout.status) {
      case "pending":
        return {
          status: "processing",
          className: "bg-blue-500/10 text-blue-500 border-blue-500/20",
        };
      case "completed":
        return {
          status: "paid",
          className: "bg-green-500/10 text-green-500 border-green-500/20",
        };
      case "failed":
        return {
          status: "failed",
          className: "bg-red-500/10 text-red-500 border-red-500/20",
        };
      default:
        return {
          status: "unknown",
          className: "bg-gray-500/10 text-gray-500 border-gray-500/20",
        };
    }
  };

  // Handle updating payout status
  const handleUpdatePayoutStatus = async (
    payoutId: number,
    status: "completed" | "failed",
    notes?: string,
    receiptUrl?: string
  ) => {
    setIsUpdatingPayout(payoutId);
    try {
      await updatePayoutStatus(payoutId, status, notes, receiptUrl);

      toast({
        title: "Payout Updated",
        description: `Payout has been marked as ${status}.`,
      });

      // Refresh payouts and stats to show updated state
      await refreshPayoutsAndStats();
    } catch (error) {
      console.error("Error updating payout status:", error);
      toast({
        title: "Error",
        description: "Failed to update payout status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPayout(null);
    }
  };

  // Handle deleting payout
  const handleDeletePayout = async (payoutId: number) => {
    setIsDeletingPayout(payoutId);
    try {
      const { error } = await supabase
        .from("collaborator_payouts")
        .delete()
        .eq("id", payoutId);

      if (error) throw error;

      toast({
        title: "Payout Deleted",
        description:
          "Payout has been deleted and associated earnings are now available for new payout requests.",
      });

      // Refresh payouts and stats to show updated state
      await refreshPayoutsAndStats();
    } catch (error) {
      console.error("Error deleting payout:", error);
      toast({
        title: "Error",
        description: "Failed to delete payout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingPayout(null);
    }
  };

  // Get customer profile for an earning
  const getCustomerProfile = (earning: CollaboratorEarning) => {
    if (!earning.user_subscriptions || !earning.user_subscriptions.user_id) {
      return null;
    }
    const userId = earning.user_subscriptions.user_id;
    return customerProfiles[userId] || null;
  };

  // Calculate pagination
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: earnings.length,
    itemsPerPage,
    currentPage,
  });

  // Handle page change
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const paginatedEarnings = earnings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout title="Collaborator Dashboard">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout title="Collaborator Dashboard">
        <div className="flex flex-col items-center justify-center h-full">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h2 className="text-xl font-bold mb-2">User Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The collaborator you're looking for does not exist or has been
            removed.
          </p>
          <Button onClick={() => navigate("/admin/users")}>
            Return to Users
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Collaborator Dashboard">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Collaborator Dashboard</h1>
        <div className="ml-auto">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/collaborators")}
          >
            <Users className="h-4 w-4 mr-2" />
            All Collaborators
          </Button>
        </div>
      </div>

      {/* User Profile Card */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="w-20 h-20">
              <AvatarImage src={user.avatar_url} />
              <AvatarFallback>{user.name?.charAt(0) || "U"}</AvatarFallback>
            </Avatar>
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold">
                  {user.name || "Unknown User"}
                </h2>
                <Badge className="bg-primary/10 text-primary">
                  Collaborator
                </Badge>
              </div>
              <div className="text-muted-foreground">{user.email}</div>
            </div>
            <div className="flex items-center">
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/users/${user.id}`)}
              >
                <User className="h-4 w-4 mr-2" />
                View User Profile
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  Total Earnings
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(stats.totalEarnings)}
                </p>
              </div>
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-500/5">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Pending</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(stats.pendingEarnings)}
                </p>
              </div>
              <CreditCard className="h-5 w-5 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-500/5">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Processing</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(stats.processingEarnings)}
                </p>
              </div>
              <Clock className="h-5 w-5 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-500/5">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Paid</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(stats.totalPaid)}
                </p>
              </div>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-500/5">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  Active Promos
                </p>
                <p className="text-2xl font-bold">{stats.activePromotions}</p>
              </div>
              <Tag className="h-5 w-5 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-indigo-500/5">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  Subscriptions
                </p>
                <p className="text-2xl font-bold">{stats.totalSubscriptions}</p>
              </div>
              <Users className="h-5 w-5 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout Request Action */}
      {stats.pendingEarnings > 0 && (
        <Card className="mb-6 border-amber-500/20 bg-amber-500/10">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-amber-400">
                  Pending Earnings Available
                </h3>
                <p className="text-sm text-amber-300">
                  {formatCurrency(stats.pendingEarnings)} in pending earnings
                  can be processed for payout
                </p>
              </div>
              <PayoutSelectionDialog
                collaboratorId={id!}
                pendingAmount={stats.pendingEarnings}
                onPayoutCreated={refreshEarningsPayoutsAndStats}
                formatCurrency={formatCurrency}
                isCreating={isCreatingPayout}
                setIsCreating={setIsCreatingPayout}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Earnings and Payouts */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="earnings">Earnings</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="promotions">Promotions</TabsTrigger>
        </TabsList>

        <TabsContent value="earnings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Earnings History</CardTitle>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : earnings.length > 0 ? (
                <>
                  <div className="relative overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs uppercase bg-muted/50">
                        <tr>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Customer</th>
                          <th className="px-4 py-3">Plan</th>
                          <th className="px-4 py-3">Promotion</th>
                          <th className="px-4 py-3">Commission</th>
                          <th className="px-4 py-3">Amount</th>
                          <th className="px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedEarnings.map((earning) => {
                          const customerProfile = getCustomerProfile(earning);
                          const planId =
                            earning.user_subscriptions?.subscription_plan_id;
                          const subscriptionPlan = planId
                            ? subscriptionPlanInfo[planId]
                            : null;

                          const { status, className } =
                            getEarningStatus(earning);

                          return (
                            <tr key={earning.id} className="border-b">
                              <td className="px-4 py-3">
                                {format(
                                  new Date(earning.created_at),
                                  "MMM dd, yyyy hh:mm a"
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {customerProfile ? (
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-6 w-6">
                                      <AvatarImage
                                        src={customerProfile.avatar_url}
                                      />
                                      <AvatarFallback>
                                        {customerProfile.name?.charAt(0) || "U"}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <div className="font-medium">
                                        {customerProfile.name}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {customerProfile.email}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <User className="h-5 w-5 text-muted-foreground" />
                                    <span className="text-muted-foreground">
                                      Unknown
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {subscriptionPlan ? (
                                  <div>
                                    <div className="font-medium">
                                      {subscriptionPlan.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {subscriptionPlan.duration}{" "}
                                      {subscriptionPlan.duration_type}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">
                                    Unknown plan
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium">
                                  {earning.promotions?.name || "Unknown"}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {earning.promotions?.code || "No code"}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {earning.commission_type_at_time_of_transaction ===
                                "percentage"
                                  ? `${earning.commission_at_time_of_transaction}%`
                                  : formatCurrency(
                                      earning.commission_at_time_of_transaction
                                    )}
                              </td>
                              <td className="px-4 py-3 font-medium">
                                {formatCurrency(earning.amount_earned)}
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant="outline" className={className}>
                                  {status.charAt(0).toUpperCase() +
                                    status.slice(1)}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Showing{" "}
                      {Math.min(
                        earnings.length,
                        1 + (currentPage - 1) * itemsPerPage
                      )}
                      -{Math.min(currentPage * itemsPerPage, earnings.length)}{" "}
                      of {earnings.length} entries
                    </div>

                    <div className="flex items-center gap-4">
                      {totalPages > 0 && (
                        <div className="flex items-center">
                          <Button
                            className="mr-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(1)}
                            disabled={!canGoPrev}
                          >
                            First
                          </Button>

                          <Button
                            className="mr-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={!canGoPrev}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>

                          {pageNumbers.map((pageNum) => (
                            <Button
                              key={pageNum}
                              className="mx-0.5 w-8 h-8 p-0"
                              variant={
                                pageNum === currentPage ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          ))}

                          <Button
                            className="ml-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={!canGoNext}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>

                          <Button
                            className="ml-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => goToPage(totalPages)}
                            disabled={!canGoNext}
                          >
                            Last
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Earnings Yet</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    When customers use this collaborator's promotion code to
                    make a purchase, their earnings will appear here.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Payout History</CardTitle>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : payouts.length > 0 ? (
                <div className="relative overflow-x-auto">
                  <table className="w-full text-sm text-left table-fixed">
                    <thead className="text-xs uppercase bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 w-[140px]">Created At</th>
                        <th className="px-4 py-3 w-[140px]">Payout Date</th>
                        <th className="px-4 py-3 w-[100px]">Amount</th>
                        <th className="px-4 py-3 w-[80px]">Status</th>
                        <th className="px-4 py-3 w-[80px]">Receipt</th>
                        <th className="px-4 py-3 w-[200px]">Notes</th>
                        <th className="px-4 py-3 w-[120px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((payout) => (
                        <tr key={payout.id} className="border-b">
                          <td className="px-4 py-3">
                            {format(
                              new Date(payout.created_at),
                              "MMM dd, yyyy 'at' hh:mm a"
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {payout.payout_date ? (
                              format(
                                new Date(payout.payout_date),
                                "MMM dd, yyyy 'at' hh:mm a"
                              )
                            ) : (
                              <span className="text-muted-foreground">
                                Not Yet Paid
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium">
                            {formatCurrency(payout.amount)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              className={
                                payout.status === "completed"
                                  ? "bg-green-500"
                                  : payout.status === "pending"
                                  ? "bg-amber-500"
                                  : "bg-red-500"
                              }
                            >
                              {payout.status.charAt(0).toUpperCase() +
                                payout.status.slice(1)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {payout.receipt_url ? (
                              <ReceiptPreviewDialog
                                receiptUrl={payout.receipt_url}
                              />
                            ) : (
                              <span className="text-muted-foreground text-xs">
                                No receipt
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            {payout.notes ? (
                              <div
                                className="text-xs text-muted-foreground truncate"
                                title={payout.notes}
                              >
                                {payout.notes}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">
                                No notes
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {payout.status === "pending" && (
                                <>
                                  <PayoutActionDialog
                                    payout={payout}
                                    action="complete"
                                    isLoading={isUpdatingPayout === payout.id}
                                    onAction={handleUpdatePayoutStatus}
                                    formatCurrency={formatCurrency}
                                  />
                                  <PayoutActionDialog
                                    payout={payout}
                                    action="fail"
                                    isLoading={isUpdatingPayout === payout.id}
                                    onAction={handleUpdatePayoutStatus}
                                    formatCurrency={formatCurrency}
                                  />
                                </>
                              )}

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-gray-600 border-gray-600 hover:bg-gray-50/10"
                                    disabled={isDeletingPayout === payout.id}
                                  >
                                    {isDeletingPayout === payout.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-3 w-3" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Delete Payout
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this
                                      payout of {formatCurrency(payout.amount)}?
                                      This will remove the payout record and
                                      make all associated earnings available for
                                      new payout requests. This action cannot be
                                      undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() =>
                                        handleDeletePayout(payout.id)
                                      }
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete Payout
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Payouts Yet</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    When payouts are processed for this collaborator, they will
                    appear here.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promotions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Active Promotions</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/promotions/new")}
              >
                <Tag className="h-4 w-4 mr-2" />
                New Promotion
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : promotions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {promotions.map((promotion) => (
                    <Card key={promotion.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="p-4 border-b">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-medium">{promotion.name}</h3>
                            <Badge
                              variant={
                                promotion.is_active ? "default" : "outline"
                              }
                              className={
                                promotion.is_active ? "bg-green-500" : ""
                              }
                            >
                              {promotion.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge
                              variant="outline"
                              className="bg-primary/10 text-primary"
                            >
                              {promotion.code}
                            </Badge>
                            <Badge variant="outline">
                              {promotion.discount_type === "percentage"
                                ? `${promotion.discount_value}% off`
                                : `₹${promotion.discount_value.toFixed(2)} off`}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {format(
                                  new Date(promotion.start_date),
                                  "MMM dd, yyyy"
                                )}{" "}
                                -
                                {promotion.end_date
                                  ? format(
                                      new Date(promotion.end_date),
                                      " MMM dd, yyyy"
                                    )
                                  : " No end date"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="px-4 py-3 bg-muted/10">
                          <div className="flex items-center justify-between text-sm">
                            <div>
                              <span className="text-muted-foreground">
                                Commission:{" "}
                              </span>
                              <span className="font-medium">
                                {promotion.collaborator_commission_type ===
                                "percentage"
                                  ? `${promotion.collaborator_commission_value}%`
                                  : formatCurrency(
                                      promotion.collaborator_commission_value
                                    )}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`/admin/promotions/${promotion.id}`)
                              }
                            >
                              Edit
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    No Promotions Yet
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-4">
                    Create a promotion and assign this user as a collaborator to
                    get started.
                  </p>
                  <Button onClick={() => navigate("/admin/promotions/new")}>
                    Create Promotion
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default CollaboratorDashboard;
